import React, { useEffect, useState } from "react"
import { Row, Col } from "react-bootstrap"
import Link from "next/link"
import { linksBuilder } from "../../../services/utils/linksBuilder"
import { useNavigateTo } from "../../../services/utils/useNavigateTo"
import { getArtworkSize } from "../../../services/utils/getArtworkSize"
import { imageResizer } from "../../../services/utils/imageResizer"
import { slugify } from "../../../services/utils/slugify"
import { chunk } from "../../../services/utils/chunk"
import { formatPrice } from "../../../services/utils/formatPrice"
import { GalleryHeader } from "../../components/GalleryHeader"
import Container from "../../components/Layout/Container"
import CustomBanner from "../../components/CustomBanner"
import NewsletterGalleries from "../../components/NewsletterGalleries"
import { HelmetTitle } from "../../components/Layout/HelmetTitle"
import Wrapper from "../../components/Layout/Wrapper"
import { SeoTags } from "../../components/Layout/SeoTags"
import styles from "../../../features/components/CustomBanner/CustomBanner.module.css"
import { DiscoverMasonry } from "../../components/DiscoverMasonry"
import { GalleryContactModal } from "../../components/GalleryContactModal"
import Button from "../../components/Layout/Button"
import { useContactAction } from "../../actions/useContactAction"
import { useAppContext } from "../../providers/AppProvider"

export const GalleryDetailPage = ({ galleryDetails }) => {
  const profile = useAppContext()

  const navigateTo = useNavigateTo()

  const onNavigate = async () => {
    await navigateTo(`/galleries/${galleryDetails.domain}/artworks`)
  }

  const {
    form,
    setForm,
    onSubmit,
    handleShow,
    handleClose,
    modal,
    success,
    error,
    errorMessage,
    onReCAPTCHAChange,
    recaptchaRef,
    loading,
  } = useContactAction(galleryDetails)

  const fullOrHalf =
    galleryDetails.featured_exhibition && galleryDetails.featured_space ? 6 : 12

  useEffect(() => {
    ArtPlacer.clear()

    galleryDetails.featured_space &&
      ArtPlacer.insert({
        gallery: galleryDetails.id,
        type: "2",
        text: "View collection",
        space: galleryDetails.featured_space.id,
        classname: `fs-12px tt-u ${styles.button}`,
        after: ".artplacerButtons",
      })
  }, [])

  const galleryPageURL = linksBuilder.galleries(galleryDetails.domain)

  return (
    <Wrapper>
      <SeoTags
        title={`${galleryDetails.name} | Discover ArtPlacer`}
        description={`Find information and learn more about ${galleryDetails.name}. See artworks, try them in your 
          room, and get in touch.`}
        image={galleryDetails.thumbnail}
        url={galleryPageURL}
        canonical={galleryPageURL}
      />
      <HelmetTitle title={galleryDetails.name} />
      <Container>
        <GalleryHeader
          galleryDetails={galleryDetails}
          onClickContact={handleShow}
        />
        <Row className="justify-content-center text-black fs-15px fs-mobile-14px">
          {galleryDetails.statement && (
            <>
              <Col
                lg={8}
                md={8}
                sm={10}
                xs={11}
                className="mb-md-2 mb-sm-0 mt-md-4 mt-sm-1">
                <p
                  className={`mb-0 word-wrap-break font-weight-light text-black lh-17 ws-pw text-container ${
                    galleryDetails.statement.length > 1000 ? "two-columns" : ""
                  }`}>
                  {galleryDetails.statement}
                </p>
              </Col>
              <hr className="w-100 mx-3 mt-4 mb-4 pb-0" />
            </>
          )}
        </Row>
        {galleryDetails.featured_artworks.length > 0 && (
          <Row>
            <Col>
              <Row className="mb-2 align-items-center">
                <Col xs={8} className="px-4">
                  <h3 className="text-black mb-1 fw-700 fs-20px">
                    Featured Artworks
                  </h3>
                </Col>
                <Col
                  xs={4}
                  className="justify-content-end row pl-2 pr-2 pl-md-4">
                  <Button
                    name="View catalog"
                    onClick={() => onNavigate()}
                    width={100}
                  />
                </Col>
              </Row>
              <DiscoverMasonry
                items={(
                  galleryDetails.featured_artworks || galleryDetails.artworks
                ).map((artwork) => ({
                  to: linksBuilder.artworks(artwork.id, artwork.title),
                  href: "/artworks/[id]",
                  title: artwork.title,
                  subtitle: artwork.artistname,
                  subSubTitle: getArtworkSize(artwork.width, artwork.height),
                  image: artwork.image,
                  sold: artwork.sold,
                  price: artwork.price
                    ? galleryDetails?.currency?.symbol +
                      " " +
                      formatPrice(artwork.price)
                    : null,
                }))}
              />
            </Col>
          </Row>
        )}
        <Row>
          {galleryDetails.featured_space && (
            <Col xs={12} md={fullOrHalf} className="mb-0 mt-4">
              <CustomBanner
                title="Check out our most popular collection in a sample room"
                buttonText="View Collection"
                left={true}
                image={imageResizer(
                  galleryDetails.featured_space.image,
                  600,
                  `AUTO`,
                )}
                moreHeight={fullOrHalf == 12}
              />
            </Col>
          )}
          {galleryDetails.featured_exhibition && (
            <Col xs={12} md={fullOrHalf} className="mb-0 mt-4">
              <CustomBanner
                title={galleryDetails.featured_exhibition.title}
                subtitle={galleryDetails.featured_exhibition.author}
                buttonText="Visit exhibition"
                buttonTo={`/exhibitions/${galleryDetails.featured_exhibition.id}`}
                buttonLink={"/exhibitions/[id]"}
                left={true}
                image={imageResizer(
                  galleryDetails.featured_exhibition.thumbnail ??
                    galleryDetails.featured_exhibition.image,
                  600,
                  `AUTO`,
                )}
                moreHeight={fullOrHalf == 12}
              />
            </Col>
          )}
        </Row>

        {galleryDetails && <NewsletterGalleries gallery={galleryDetails} />}

        {galleryDetails.collections.length > 0 &&
          galleryDetails.collections.length < 80 && (
            <>
              <Row>
                <hr className="w-100 mx-3 mt-4 mb-4 pb-0" />
                <Col>
                  <h3
                    className="text-black mb-3 mt-0 fw-700 fs-20px"
                    style={{ marginTop: 32 }}>
                    Collections
                  </h3>
                </Col>
              </Row>
              {chunk(galleryDetails.collections, 6).map((c) => (
                <Row>
                  {c.map((i) => (
                    <Col
                      sm="6"
                      md="2"
                      className="text-capitalize font-weight-light mb-3 fs-15px">
                      <Link
                        style={{
                          color: "black",
                          lineHeight: "1.2",
                          whiteSpace: "pre",
                        }}
                        href={linksBuilder.collections(
                          galleryDetails.domain,
                          i.collection,
                        )}>
                        {i.label ?? i.collection}
                      </Link>
                    </Col>
                  ))}
                </Row>
              ))}
            </>
          )}
        <GalleryContactModal
          form={form}
          setForm={setForm}
          galleryDetails={galleryDetails}
          modal={modal}
          onSubmit={onSubmit}
          handleClose={handleClose}
          error={error}
          errorMessage={errorMessage}
          success={success}
          recaptchaRef={recaptchaRef}
          onReCAPTCHAChange={onReCAPTCHAChange}
        />
      </Container>
    </Wrapper>
  )
}
